import { SearchOutlined } from "@mui/icons-material";
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip,
  Divider, InputAdornment, Paper, Stack, TextField, Typography
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useQuery } from "@tanstack/react-query";
import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { loadWorkflowDiagnostics } from "./api";
import { PageHeader } from "./PageHeader";

export function WorkflowDiagnosticPage({ onBack }: { onBack: () => void }) {
  const { t, i18n } = useTranslation();
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const diagnostic = useQuery({
    queryKey: ["workflow-diagnostics", query],
    queryFn: () => loadWorkflowDiagnostics(query), retry: false
  });
  const submit = (event: FormEvent) => {
    event.preventDefault(); setQuery(input.trim());
  };
  const formatDate = (date: string) => new Intl.DateTimeFormat(i18n.language, {
    dateStyle: "short", timeStyle: "medium"
  }).format(new Date(date));
  return <Box sx={{ width: "100%" }}>
    <PageHeader title={t("administration.entries.workflowDiagnostics")} onBack={onBack}
      description={t("transverse.workflowDiagnostic.description")} />
    <Paper component="form" onSubmit={submit} variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <TextField fullWidth value={input} onChange={(event) => setInput(event.target.value)}
          placeholder={t("transverse.workflowDiagnostic.searchPlaceholder")}
          slotProps={{ input: { startAdornment: <InputAdornment position="start">
            <SearchOutlined />
          </InputAdornment> } }} />
        <Button type="submit" variant="contained">{t("transverse.diagnostic.run")}</Button>
      </Stack>
    </Paper>
    {diagnostic.isError && <Alert severity="error" sx={{ mb: 2 }}>
      {t("transverse.workflowDiagnostic.loadError")}
    </Alert>}
    {diagnostic.data?.length === 0 && <Alert severity="info">
      {t("transverse.workflowDiagnostic.empty")}
    </Alert>}
    <Stack spacing={1.5}>{diagnostic.data?.map((workflow) =>
      <Accordion key={workflow.id} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1}
            alignItems={{ md: "center" }} sx={{ width: "100%", pr: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={800}>
                {workflow.subjectReference ?? workflow.subjectId} · {workflow.messageType}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {workflow.id} · {formatDate(workflow.updatedAt)}
              </Typography>
            </Box>
            {workflow.currentValidationLevel && <Chip size="small"
              label={`L${workflow.currentValidationLevel} · ${workflow.currentBusinessPartnerName ?? "—"}`} />}
            <Chip size="small" color={workflow.state === "COMPLETED" ? "success" : "primary"}
              label={workflow.state} />
          </Stack>
        </AccordionSummary>
        <AccordionDetails><Stack spacing={2}>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {workflow.checks.map((check) => <Alert key={check.code}
              severity={check.severity === "OK" ? "success" : check.severity === "WARNING" ? "warning" : "error"}
              sx={{ py: 0 }}>{check.message}</Alert>)}
          </Stack>
          <Box><Typography fontWeight={800}>{t("transverse.workflowDiagnostic.payload")}</Typography>
            <Box component="pre" sx={{ m: 0, p: 1, bgcolor: "action.hover", overflowX: "auto" }}>
              {JSON.stringify(workflow.decidedPayload ?? workflow.requestedPayload, null, 2)}
            </Box></Box>
          <Divider />
          <Typography fontWeight={800}>{t("transverse.workflowDiagnostic.levels")}</Typography>
          {workflow.steps.map((step) => <Paper key={step.id} variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
              <Chip label={`L${step.validationLevel}`} color={step.status === "ACTIVE" ? "primary" : "default"} />
              <Box sx={{ flex: 1 }}><Typography fontWeight={700}>{step.businessPartnerName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {step.businessPartnerCode} · {step.businessPartnerKind}
                </Typography></Box>
              <Chip size="small" label={step.status} />
              {step.selectedAction && <Chip size="small" color="success" label={step.selectedAction} />}
            </Stack>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {step.decisionComment ?? t("transverse.workflowDiagnostic.noDecision")}
              {step.decidedBy ? ` · ${step.decidedBy}` : ""}
            </Typography>
          </Paper>)}
          <Divider />
          <Typography fontWeight={800}>{t("transverse.workflowDiagnostic.timeline")}</Typography>
          {workflow.events.length === 0 && <Typography color="text.secondary">
            {t("transverse.workflowDiagnostic.noEvent")}
          </Typography>}
          {workflow.events.map((event, index) => <Box key={`${event.occurredAt}-${index}`}
            sx={{ pl: 1.5, borderLeft: 3, borderColor: "divider" }}>
            <Typography fontWeight={700}>{event.stage} · {event.action}</Typography>
            <Typography variant="body2">{event.comment}</Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDate(event.occurredAt)}{event.actor ? ` · ${event.actor}` : ""}
            </Typography>
          </Box>)}
        </Stack></AccordionDetails>
      </Accordion>)}</Stack>
  </Box>;
}
